import React, { useState } from 'react';
import axios from 'axios';
import {
  createEmptyProgram,
  getProgramTabLabel,
  normalizeProgramsFromApi,
  PROGRAM_TITLE_SIZE_OPTIONS,
  serializeProgramsForApi
} from '../../utils/programUtils';

const ProgramDocumentsEditor = ({ programs, setPrograms, onSuccess }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragTabIndex, setDragTabIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const activeProgram = programs[activeIndex] || null;

  const updatePrograms = (updater) => {
    setPrograms((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  };

  const updateActiveProgram = (updater) => {
    updatePrograms((prev) =>
      prev.map((program, index) => (index === activeIndex ? updater(program) : program))
    );
  };

  const handleBannerChange = (event) => {
    const { name, value, type, checked } = event.target;
    updateActiveProgram((program) => ({
      ...program,
      [name]: type === 'checkbox' ? checked : name === 'titleSize' ? Number(value) : value
    }));
  };

  const handleTableChange = (rowIndex, field, value) => {
    updateActiveProgram((program) => {
      const events = [...program.events];
      events[rowIndex] = { ...events[rowIndex], [field]: value };
      return { ...program, events };
    });
  };

  const handleAddRow = () => {
    updateActiveProgram((program) => ({
      ...program,
      events: [...program.events, { EventDate: '', Event: '', Speakers: '', isSelected: false }]
    }));
  };

  const handleDeleteRow = (rowIndex) => {
    updateActiveProgram((program) => ({
      ...program,
      events: program.events.filter((_, index) => index !== rowIndex)
    }));
  };

  const handleCleanRow = (rowIndex) => {
    updateActiveProgram((program) => {
      const events = [...program.events];
      events[rowIndex] = { EventDate: '', Event: '', Speakers: '', isSelected: false };
      return { ...program, events };
    });
  };

  const handleSelectRow = (rowIndex) => {
    updateActiveProgram((program) => {
      const events = [...program.events];
      events[rowIndex] = { ...events[rowIndex], isSelected: !events[rowIndex].isSelected };
      return { ...program, events };
    });
  };

  const handleAddProgram = () => {
    updatePrograms((prev) => {
      const next = [...prev, createEmptyProgram()];
      setActiveIndex(next.length - 1);
      return next;
    });
  };

  const handleDeleteProgram = () => {
    if (programs.length <= 1) {
      alert('Нельзя удалить последний документ');
      return;
    }

    const label = getProgramTabLabel(activeProgram, activeIndex);
    if (!window.confirm(`Удалить «${label}»?`)) {
      return;
    }

    updatePrograms((prev) => prev.filter((_, index) => index !== activeIndex));
    setActiveIndex((prev) => Math.max(0, prev - 1));
  };

  const handleDragStart = (event, index) => {
    setDragTabIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => setDragOverIndex(null);

  const handleDrop = (event, dropIndex) => {
    event.preventDefault();
    const fromIndex = dragTabIndex ?? Number(event.dataTransfer.getData('text/plain'));

    if (Number.isNaN(fromIndex) || fromIndex === dropIndex) {
      setDragTabIndex(null);
      setDragOverIndex(null);
      return;
    }

    updatePrograms((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(dropIndex, 0, moved);
      return next;
    });

    setActiveIndex((prev) => {
      if (prev === fromIndex) return dropIndex;
      if (fromIndex < prev && dropIndex >= prev) return prev - 1;
      if (fromIndex > prev && dropIndex <= prev) return prev + 1;
      return prev;
    });

    setDragTabIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragTabIndex(null);
    setDragOverIndex(null);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (isSaving) return;

    setIsSaving(true);
    try {
      const response = await axios.put('/api/programs', {
        sections: serializeProgramsForApi(programs)
      });
      const savedPrograms = normalizeProgramsFromApi(response.data?.sections || []);
      setPrograms(savedPrograms.length ? savedPrograms : [createEmptyProgram()]);
      setActiveIndex((prev) => Math.min(prev, Math.max(savedPrograms.length - 1, 0)));
      onSuccess('Программы успешно сохранены', savedPrograms);
    } catch (error) {
      console.error('Error saving programs:', error);
      alert(error.response?.data?.message || 'Ошибка при сохранении программ');
    } finally {
      setIsSaving(false);
    }
  };

  if (!activeProgram) {
    return null;
  }

  return (
    <div className="program-documents-editor">
      <div className="program-tabs-scroll">
        <div className="program-tabs" role="tablist" aria-label="Документы программ">
          {programs.map((program, index) => (
            <button
              key={program.clientKey || program.id || index}
              type="button"
              role="tab"
              aria-selected={index === activeIndex}
              draggable
              className={`program-tab ${index === activeIndex ? 'is-active' : ''} ${dragOverIndex === index ? 'is-drag-over' : ''}`}
              onClick={() => setActiveIndex(index)}
              onDragStart={(event) => handleDragStart(event, index)}
              onDragOver={(event) => handleDragOver(event, index)}
              onDragLeave={handleDragLeave}
              onDrop={(event) => handleDrop(event, index)}
              onDragEnd={handleDragEnd}
            >
              <span className="program-tab__index">{index + 1}</span>
              <span className="program-tab__label">{getProgramTabLabel(program, index)}</span>
              <span className="program-tab__drag" aria-hidden="true">⋮⋮</span>
            </button>
          ))}
          <button type="button" className="program-tab program-tab--add" onClick={handleAddProgram}>
            + Новый документ
          </button>
        </div>
      </div>

      <form className="admin-form" onSubmit={handleSave}>
        <div className="program-editor-toolbar">
          <p className="program-editor-hint">Перетащите вкладки для изменения порядка на главной</p>
          {programs.length > 1 && (
            <button type="button" className="admin-action-btn admin-action-btn--ghost-danger" onClick={handleDeleteProgram}>
              Удалить документ
            </button>
          )}
        </div>

        <div className="admin-form-grid">
          <div className="admin-field admin-field--wide">
            <div className="program-title-heading">
              <label htmlFor="program-title">Заглавие</label>
              <div className="program-title-heading__controls">
                <label className="admin-inline-check">
                  <input
                    type="checkbox"
                    name="titleVisible"
                    checked={activeProgram.titleVisible !== false}
                    onChange={handleBannerChange}
                  />
                  Показывать на сайте
                </label>
                <label className="program-title-size" htmlFor="program-title-size">
                  <span>Размер</span>
                  <select
                    id="program-title-size"
                    name="titleSize"
                    value={activeProgram.titleSize || 25}
                    onChange={handleBannerChange}
                    disabled={activeProgram.titleVisible === false}
                  >
                    {PROGRAM_TITLE_SIZE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
            <input
              id="program-title"
              type="text"
              name="title"
              value={activeProgram.title}
              onChange={handleBannerChange}
              placeholder="Название программы"
            />
          </div>
          <div className="admin-field">
            <label htmlFor="program-date">Дата</label>
            <input id="program-date" type="text" name="date" value={activeProgram.date} onChange={handleBannerChange} placeholder="06.07.2026" />
          </div>
          <div className="admin-field">
            <label htmlFor="program-format">Формат</label>
            <input id="program-format" type="text" name="format" value={activeProgram.format} onChange={handleBannerChange} placeholder="Онлайн" />
          </div>
          <div className="admin-field">
            <label htmlFor="program-location">Место проведения</label>
            <input id="program-location" type="text" name="location" value={activeProgram.location} onChange={handleBannerChange} placeholder="Необязательно" />
          </div>
        </div>

        <div className="program-table-header">
          <h4>Расписание</h4>
          <span className="program-table-count">{activeProgram.events.length} строк</span>
        </div>

        {activeProgram.events.length === 0 ? (
          <div className="admin-empty-state admin-empty-state--compact">
            <p>Нет строк в таблице</p>
            <button type="button" className="admin-action-btn admin-action-btn--secondary" onClick={handleAddRow}>
              Добавить первую строку
            </button>
          </div>
        ) : (
          activeProgram.events.map((event, rowIndex) => (
            <div key={rowIndex} className={`table-row ${event.isSelected ? 'is-selected' : ''}`}>
              <div className="table-row__number">{rowIndex + 1}</div>
              <div className="table-row__fields">
                <div className="admin-field">
                  <label>Время</label>
                  <input
                    type="text"
                    value={event.EventDate}
                    onChange={(e) => handleTableChange(rowIndex, 'EventDate', e.target.value)}
                    placeholder="13:00–13:20"
                  />
                </div>
                <div className="admin-field">
                  <label>Мероприятие</label>
                  <textarea
                    value={event.Event}
                    onChange={(e) => handleTableChange(rowIndex, 'Event', e.target.value)}
                    placeholder="Название мероприятия"
                    rows={2}
                  />
                </div>
                <div className="admin-field">
                  <label>Докладчик</label>
                  <textarea
                    value={event.Speakers}
                    onChange={(e) => handleTableChange(rowIndex, 'Speakers', e.target.value)}
                    placeholder="ФИО докладчика"
                    rows={2}
                  />
                </div>
              </div>
              <div className="row-actions">
                <button type="button" onClick={() => handleCleanRow(rowIndex)} className="row-action-btn row-action-btn--warn">
                  Стереть
                </button>
                <button type="button" onClick={() => handleDeleteRow(rowIndex)} className="row-action-btn row-action-btn--danger">
                  Удалить
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectRow(rowIndex)}
                  className={`row-action-btn row-action-btn--select ${event.isSelected ? 'is-active' : ''}`}
                >
                  {event.isSelected ? 'Выделено' : 'Выделить'}
                </button>
              </div>
            </div>
          ))
        )}

        <div className="admin-form-footer">
          <button type="button" onClick={handleAddRow} className="admin-action-btn admin-action-btn--secondary">
            + Строка
          </button>
          <button type="submit" className="admin-action-btn admin-action-btn--primary" disabled={isSaving}>
            {isSaving ? 'Сохранение...' : 'Сохранить программы'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProgramDocumentsEditor;
